import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { ChamadoDetalheDto, ConversaDto, UsuarioSessao } from '@crm/shared'
import { useMensagens } from '../../api/hooks-chat'
import { SeletorDeEmpresa } from '../../componentes/SeletorDeEmpresa'
import { useAtualizarContato, useContato } from '../../api/hooks-leads'
import { Avatar } from '../../componentes/Avatar'
import { NomeDoContato } from '../../componentes/NomeDoContato'
import { BotaoIcone } from '../../componentes/BotaoIcone'
import { IconeArquivo, IconeLapis, IconeTocar, IconeX } from '../../componentes/icones'
import { midiaDaMensagem, type MidiaParaVer } from '../../componentes/VisualizadorDeMidia'
import { Botao, Campo, Carregando, Modal, juntar } from '../../componentes/ui'
import { SeletorEtiquetas } from './AcoesDoContato'
import { AgendamentosDoContato } from './AgendamentosDoContato'
import { NegociacaoDoContato } from './NegociacaoDoContato'
import { pode } from '../../permissao'

/**
 * A ficha do contato, ao lado da conversa.
 *
 * Abre pelo nome ou pela foto no cabeçalho — o gesto que a equipe já traz do
 * Digisac. Antes, o que se sabia do contato cabia numa linha de subtítulo, e o
 * que não coubesse ali simplesmente não existia na tela: mídias trocadas,
 * protocolo, anotação do atendimento.
 *
 * É painel e não modal porque se consulta **durante** a conversa: quem procura
 * o protocolo para ditar ao cliente precisa continuar vendo o que ele acabou
 * de escrever.
 */

export function FichaDoContato({
  conversa,
  chamado,
  aberta,
  aoFechar,
  aoAbrirMidia,
  usuario,
}: {
  conversa: ConversaDto
  chamado: ChamadoDetalheDto | undefined
  aberta: boolean
  aoFechar: () => void
  /** Reaproveita o visualizador que a conversa já usa para imagem recebida. */
  aoAbrirMidia: (midia: MidiaParaVer) => void
  usuario: UsuarioSessao
}) {
  const { data: contato } = useContato(aberta ? conversa.contatoId : null)
  const { data: mensagens } = useMensagens(aberta ? conversa.chamadoId : null)
  const atualizarContato = useAtualizarContato()
  const clienteDeConsultas = useQueryClient()

  const [editandoNome, setEditandoNome] = useState(false)
  const [nome, setNome] = useState(conversa.nome)

  const [copiado, setCopiado] = useState(false)
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null)

  /**
   * Recarrega a anotação ao trocar de conversa, e não a cada repintura: senão
   * o que está sendo digitado é sobrescrito pela resposta do servidor.
   *
   * Os dois lados da comparação precisam ser `null` quando não há chamado.
   * Antes um lado guardava `null` e o outro chegava `undefined` enquanto a
   * consulta do chamado ainda estava no ar — `null !== undefined` é sempre
   * verdadeiro, então o bloco se repetia a cada renderização e levantava
   * "Too many re-renders", que na tela é a página em branco. Acontecia ao
   * clicar em outra conversa com a ficha aberta: é justamente o instante em
   * que o chamado novo ainda não chegou.
   */
  const idDoChamado = chamado?.id ?? null
  if (aberta && carregadoDe !== idDoChamado) {
    setCarregadoDe(idDoChamado)
    setNome(conversa.nome)
    setEditandoNome(false)
  }

  useEffect(() => {
    if (!copiado) return
    const t = setTimeout(() => setCopiado(false), 2000)
    return () => clearTimeout(t)
  }, [copiado])

  if (!aberta) return null

  /*
    As mídias saem das mensagens já carregadas, sem consulta nova.

    O painel abre em cima de uma conversa que a tela acabou de buscar; pedir a
    mesma coisa de novo ao servidor só para listar os anexos seria trabalho
    repetido a cada abertura.
  */
  const midias = (mensagens ?? []).filter((m) => m.midiaUrl)

  // Só há para onde voltar quando o WhatsApp deu um nome e ele é outro.
  const podeReverter = Boolean(conversa.nomeOriginal && conversa.nomeOriginal !== conversa.nome)

  function gravarNome(novo: string) {
    const limpo = novo.trim()
    if (!limpo) return
    atualizarContato.mutate(
      { id: conversa.contatoId, dados: { nome: limpo } },
      {
        onSuccess: () => {
          setEditandoNome(false)
          // A lista de conversas guarda o nome numa consulta própria; sem
          // isto o cartão continuaria com o nome antigo até recarregar.
          void clienteDeConsultas.invalidateQueries({ queryKey: ['conversas'] })
        },
      },
    )
  }

  async function copiarProtocolo() {
    if (!chamado?.protocolo) return
    try {
      await navigator.clipboard.writeText(chamado.protocolo)
      setCopiado(true)
    } catch {
      // Área de transferência bloqueada (contexto sem permissão): o número
      // continua à vista para copiar na mão, então não vale interromper.
    }
  }

  return (
    <aside
      className="flex w-80 shrink-0 flex-col overflow-y-auto border-l border-tinta-200 bg-superficie"
    >
      {/*
        Fechar de um lado, renomear do outro. O lápis fica na ponta oposta ao
        X porque são as duas ações da ficha inteira, e postas juntas seria
        fácil clicar em fechar querendo editar.
      */}
      <header className="flex items-center justify-between px-3 py-2">
        <BotaoIcone rotulo="Fechar a ficha" onClick={aoFechar}>
          <IconeX className="h-4 w-4" />
        </BotaoIcone>

        <BotaoIcone
          rotulo={editandoNome ? 'Cancelar a edição' : 'Renomear o contato'}
          dicaAlinhada="direita"
          onClick={() => {
            setNome(conversa.nome)
            setEditandoNome((e) => !e)
          }}
          className={editandoNome ? 'bg-tinta-100 text-tinta-900' : undefined}
        >
          <IconeLapis className="h-4 w-4" />
        </BotaoIcone>
      </header>

      <div className="flex flex-col items-center px-4 pb-4 text-center">
        {/*
          A foto amplia no clique, como no WhatsApp. Contato que manda foto de
          peça impressa costuma ter a própria foto de perfil como referência
          visual, e 40px não servem para conferir nada.
        */}
        <button
          type="button"
          onClick={() =>
            aoAbrirMidia({
              url: `/api/contatos/${conversa.contatoId}/foto`,
              tipo: 'imagem',
              nome: conversa.nome,
            })
          }
          className="scale-150 rounded-full transition hover:brightness-90"
          title="Ampliar a foto"
        >
          <Avatar contatoId={conversa.contatoId} nome={conversa.nome} />
        </button>

        <p className="mt-4 flex max-w-full flex-wrap items-baseline justify-center font-semibold text-tinta-900">
          <NomeDoContato nome={conversa.nome} nomeOriginal={conversa.nomeOriginal} />
        </p>

        <p className="mt-1 text-sm text-tinta-600">
          {conversa.telefone ?? 'sem número'}
          {conversa.telefoneMascarado ? ' (mascarado)' : ''}
        </p>

        {/*
          A empresa fica junto de quem a pessoa é, e não entre as seções de
          baixo: é aqui que se resolve o "outro número". Quem atende o celular
          novo liga à empresa, e o contato vai para o dono certo.
        */}
        {contato ? (
          <div className="mt-3 w-full text-left">
            <SeletorDeEmpresa
              contato={contato}
              podeEditar={pode(usuario, 'leads.editar')}
              podeCriarEmpresa={pode(usuario, 'leads.criar')}
            />
          </div>
        ) : null}

        {/*
          A negociação logo abaixo da empresa, no mesmo desenho (pedido de
          06/10/2026). Só para quem vê Leads: a lista vem de lá, e o servidor
          recusaria a consulta.
        */}
        {pode(usuario, 'leads.ver') ? (
          <div className="mt-2 w-full text-left">
            <NegociacaoDoContato
              contatoId={conversa.contatoId}
              contatoNome={conversa.nome}
              usuario={usuario}
            />
          </div>
        ) : null}
      </div>

      <Secao titulo="Mídias e documentos" contagem={midias.length}>
        {midias.length === 0 ? (
          <p className="py-2 text-xs text-tinta-400">Nada trocado nesta conversa ainda.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {/*
              Cada miniatura abre no visualizador, por cima da tela.

              Eram links para "abrir em nova aba", e nesta janela a nova aba
              vira navegação na mesma aba: a foto tomava o lugar do CRM e só o
              botão de voltar do navegador tirava a pessoa de lá.
            */}
            {midias.slice(0, 9).map((m) => {
              const midia = midiaDaMensagem(m)
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => midia && aoAbrirMidia(midia)}
                  className="relative flex aspect-square items-center justify-center overflow-hidden rounded ring-1 ring-tinta-200 bg-tinta-50 transition hover:ring-marca"
                  title={m.midiaNome ?? m.conteudo ?? m.tipo}
                  aria-label={`Abrir ${m.tipo === 'video' ? 'vídeo' : m.tipo === 'imagem' ? 'imagem' : 'arquivo'}`}
                >
                  {m.tipo === 'imagem' ? (
                    <img src={m.midiaUrl as string} alt="" className="h-full w-full object-cover" />
                  ) : m.tipo === 'video' ? (
                    /*
                      O próprio quadro do vídeo como miniatura, e não um ícone
                      de arquivo: na ficha do Erick, cinco dos nove quadrados
                      eram o mesmo desenho de folha, e não dava para saber qual
                      era vídeo. O `#t=0.1` faz o navegador mostrar um quadro
                      em vez de um retângulo preto.
                    */
                    <>
                      <video
                        src={`${m.midiaUrl as string}#t=0.1`}
                        preload="metadata"
                        muted
                        className="pointer-events-none h-full w-full object-cover"
                      />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white">
                          <IconeTocar className="h-3.5 w-3.5" />
                        </span>
                      </span>
                    </>
                  ) : (
                    <IconeArquivo className="h-6 w-6 text-tinta-400" />
                  )}
                </button>
              )
            })}
          </div>
        )}
      </Secao>

      {/*
        O protocolo é o número que o cliente cita ao ligar. Fica com botão de
        copiar porque ditar onze dígitos lendo da tela é onde o erro acontece.
      */}
      {/* [INÍCIO] Resumo Gemini: o que a conversa diz, e se aponta matrícula */}
      {chamado?.resumoIa ? (
        <Secao titulo="Resumo da conversa">
          {/* [INÍCIO]/[FIM] Jornada do portal: Quem / Interesse / Pendências vêm em linhas próprias */}
          <p className="text-sm leading-snug whitespace-pre-line text-tinta-700">{chamado.resumoIa}</p>
          <p className="mt-2 text-xs text-tinta-500">
            {chamado.matriculaIa === 'sim'
              ? 'Indica matrícula'
              : chamado.matriculaIa === 'nao'
                ? 'Não indica matrícula'
                : 'Matrícula ainda indefinida'}
          </p>
        </Secao>
      ) : null}
      {/* [FIM] Resumo Gemini */}

      <Secao titulo="Protocolo de atendimento">
        <div className="flex items-center gap-2">
          <span className="flex-1 font-mono text-sm text-tinta-700">
            {chamado?.protocolo ?? '-'}
          </span>
          {chamado?.protocolo ? (
            <Botao variante="primario" onClick={copiarProtocolo}>
              {copiado ? 'Copiado' : 'Copiar'}
            </Botao>
          ) : null}
        </div>
      </Secao>

      {/*
        A ordem depois do protocolo segue o que se faz numa conversa de venda,
        do mais frequente ao mais raro: marcar etiqueta e, por último, conferir
        o que ainda vai sair sozinho.
      */}

      <Secao titulo="Etiquetas">
        <SeletorEtiquetas conversa={conversa} />
      </Secao>

      {/*
        Só o que ainda vai sair, e não o que já saiu.

        A mensagem enviada já está na própria conversa, logo ao lado; repetir
        cada uma aqui transformava o bloco num segundo histórico que crescia
        para sempre. Fica a fila — o que ainda dá para desmarcar.

        Preso a `agendar_mensagem`: quem não pode programar envio também não
        precisa ver a fila — e, sem a chave, o servidor recusaria o cancelamento
        de qualquer forma.
      */}
      {aberta && pode(usuario, 'agendar_mensagem') ? (
        <Secao titulo="Mensagens programadas">
          <div>
            <AgendamentosDoContato contatoId={conversa.contatoId} />
          </div>
        </Secao>
      ) : null}

      {contato && contato.duplicados.length > 0 ? (
        <Secao titulo="Possível segundo número">
          <p className="text-xs text-amber-700">
            Mesmo documento em {contato.duplicados.map((d) => d.nome).join(', ')}.
          </p>
        </Secao>
      ) : null}

      {!contato ? <Carregando texto="Carregando a ficha…" /> : null}

      {/*
        Renomear numa janela, e não num campo dentro da coluna.

        A ficha tem 320px e rola: editando ali, o campo saía da vista ao rolar
        e os botões ficavam espremidos em três linhas. No meio da tela o nome
        cabe inteiro, e a janela deixa claro que aquilo é uma decisão a
        confirmar — não algo que já mudou.
      */}
      <Modal
        aberto={editandoNome}
        titulo={`Renomear ${conversa.nome}`}
        aoFechar={() => setEditandoNome(false)}
        // Formulário curto, mas com texto digitado: um clique fora sem querer
        // jogaria fora o que a pessoa acabou de escrever.
        fecharAoClicarFora={false}
        rodape={
          <>
            {podeReverter ? (
              <Botao
                className="mr-auto"
                onClick={() => gravarNome(conversa.nomeOriginal as string)}
              >
                Voltar ao original
              </Botao>
            ) : null}
            <Botao onClick={() => setEditandoNome(false)}>Cancelar</Botao>
            <Botao
              variante="primario"
              carregando={atualizarContato.isPending}
              disabled={!nome.trim() || nome.trim() === conversa.nome}
              onClick={() => gravarNome(nome)}
            >
              Salvar
            </Botao>
          </>
        }
      >
        <div className="space-y-2">
          <Campo
            rotulo="Nome do contato"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') gravarNome(nome)
            }}
            autoFocus
          />

          {podeReverter ? (
            <p className="text-xs text-tinta-400">
              O WhatsApp informou{' '}
              <strong className="text-tinta-600">{conversa.nomeOriginal}</strong>. “Voltar ao
              original” devolve esse nome.
            </p>
          ) : (
            <p className="text-xs text-tinta-400">
              {conversa.nomeOriginal
                ? 'Este já é o nome que veio do WhatsApp.'
                : 'Contato cadastrado à mão: não há nome do WhatsApp para voltar.'}
            </p>
          )}
        </div>
      </Modal>
    </aside>
  )
}

function Secao({
  titulo,
  contagem,
  children,
}: {
  titulo: string
  contagem?: number
  children: React.ReactNode
}) {
  return (
    <section className={juntar('border-t border-tinta-100 px-4 py-3')}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-tinta-800">{titulo}</h3>
        {contagem !== undefined ? <span className="text-xs text-tinta-400">{contagem}</span> : null}
      </div>
      {children}
    </section>
  )
}
